import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";

const NAMES = { boho: "The Boho Picnic", floating: "Floating Picnic", electronic: "Picnic Electronic" };

export default function BookingConfirmation() {
  const { t, lang } = useLanguage();
  const { id } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(!!sessionId);

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setBooking(r.data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${id}?session_id=${sessionId}`);
        if (data.payment_status === "paid") {
          const refreshed = await api.get(`/bookings/${id}`);
          setBooking(refreshed.data);
          setChecking(false);
          clearInterval(poll);
        }
      } catch { /* keep polling */ }
      if (attempts >= 8) {
        setChecking(false);
        clearInterval(poll);
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [sessionId, id]);

  return (
    <div data-testid="confirmation-page" className="pt-40 pb-28 max-w-2xl mx-auto px-5 sm:px-8 text-center">
      <SEO title="Confirmation — The Nice Picnic" description="Your booking confirmation at The Nice Picnic." />
      {error ? (
        <p className="text-earth">{t("booking.bookingError")}</p>
      ) : !booking ? (
        <p className="text-earth">{t("booking.loadingSlots")}</p>
      ) : (
        <Reveal>
          <CheckCircle2 size={52} className="text-olive mx-auto" />
          <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-8">{t("booking.confirmationTitle")}</h1>
          <p className="mt-6 text-earth font-light leading-relaxed">
            {booking.payment_status === "paid"
              ? t("booking.confirmationPaidText").replace("{name}", booking.customer.name.split(" ")[0])
              : t("booking.confirmationText").replace("{name}", booking.customer.name.split(" ")[0])}
          </p>
          <div className="mt-10 border border-champagne rounded-3xl p-8 bg-cream/50 text-left space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-earth">{t("booking.confirmationRef")}</span>
              <span data-testid="confirmation-ref" className="text-ink font-medium font-mono text-xs">{booking.id.slice(0, 8).toUpperCase()}</span></div>
            <div className="flex justify-between"><span className="text-earth">{t("booking.step1")}</span>
              <span className="text-ink">{NAMES[booking.experience]}{booking.package ? ` — ${booking.package}` : ""}</span></div>
            {booking.event_title && <div className="flex justify-between"><span className="text-earth">Event</span><span className="text-ink">{booking.event_title}</span></div>}
            <div className="flex justify-between"><span className="text-earth">Date</span>
              <span className="text-ink">{new Date(`${booking.date}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span></div>
            <div className="flex justify-between"><span className="text-earth">{t("booking.step4")}</span>
              <span className="text-ink">{booking.time_slot.replace("-", " – ")}</span></div>
            <div className="flex justify-between"><span className="text-earth">{t("booking.guests")}</span>
              <span className="text-ink">{booking.guests}</span></div>
            <div className="flex justify-between border-t border-champagne pt-3"><span className="text-earth">{t("booking.total")}</span>
              <span data-testid="confirmation-total" className="font-serif text-2xl text-clay">{booking.amount.toFixed(0)} €</span></div>
            <div className="flex justify-between"><span className="text-earth">Payment</span>
              <span data-testid="confirmation-payment-status" className={`text-xs uppercase tracking-[0.18em] ${booking.payment_status === "paid" ? "text-olive-deep" : "text-clay"}`}>
                {booking.payment_status === "paid" ? t("booking.confirmationPaid") : checking ? t("booking.confirmationChecking") : t("booking.confirmationPending")}
              </span></div>
          </div>
          <p className="mt-8 text-sm text-earth/70">{t("booking.confirmationQuestions")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a data-testid="confirmation-whatsapp" href={`https://wa.me/33652882160?text=${encodeURIComponent(`Booking ${booking.id.slice(0, 8).toUpperCase()} — ${NAMES[booking.experience]}`)}`}
              target="_blank" rel="noopener noreferrer" className="btn-outline !px-6 !py-3">WhatsApp</a>
            <Link data-testid="confirmation-home" to="/" className="btn-primary !px-6 !py-3">The Nice Picnic</Link>
          </div>
        </Reveal>
      )}
    </div>
  );
}
